import { useQuery } from '@tanstack/react-query';
import type { CustomFieldType, Stage } from '#shared';
import { api } from './api';

export interface Tag { id: string; name: string; color: string }
export interface Status { id: string; name: string; color: string; stage: Stage; sortOrder: number; isDefault: boolean; leadCount: number }
export interface Source { id: string; name: string; isActive: boolean }
export interface Campaign { id: string; name: string; description: string | null; isActive: boolean; leadCount: number }
export interface CustomField {
  id: string; key: string; label: string; type: CustomFieldType; options: string[]; isRequired: boolean; showInList: boolean;
}
export interface TeamOption { id: string; name: string; roleName: string; receivesLeads: boolean; dailyLeadCap: number | null }
export interface LeadSetup { statuses: Status[]; sources: Source[]; tags: Tag[]; campaigns: Campaign[]; customFields: CustomField[]; users: TeamOption[] }

export interface LeadRow {
  id: string; number: number; name: string; phone: string | null; email: string | null; company: string | null; city: string | null;
  value: number | null; isQualified: boolean; score: number | null;
  /** AI temperature and the reason behind it. */
  aiLabel: 'hot' | 'warm' | 'cold' | null; aiReason: string | null;
  nextFollowupAt: string | null; followupNote: string | null; lastActivityAt: string; lastContactedAt: string | null; createdAt: string;
  custom: Record<string, unknown>;
  statusId: string; statusName: string; statusColor: string; stage: Stage;
  sourceId: string | null; sourceName: string | null; campaignId: string | null; campaignName: string | null;
  ownerId: string | null; ownerName: string | null; tags: Tag[]; lastNote: string | null;
  deletedAt?: string | null; deletedByName?: string | null;
}
export interface Activity {
  id: number; type: string; body: string | null; meta: Record<string, unknown> | null; createdAt: string; userName: string | null; toName: string | null;
}
export interface LeadDetail extends LeadRow {
  altPhone: string | null; state: string | null; address: string | null; notes: string | null;
  createdByName: string | null; wonAt: string | null; lostAt: string | null; activities: Activity[];
}

/** Statuses, sources, tags, campaigns, custom fields and people — cached, used by every lead screen. */
export function useLeadSetup() {
  return useQuery({ queryKey: ['lead-setup'], queryFn: () => api.get<LeadSetup>('/lead-setup'), staleTime: 5 * 60_000 });
}

/* Follow-up quick picks -------------------------------------------------------------------- */

const at = (d: Date, h: number, m = 0) => { const x = new Date(d); x.setHours(h, m, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export function followupPresets(now = new Date()): { label: string; date: Date }[] {
  const inHour = new Date(now.getTime() + 60 * 60_000);
  const eveningToday = at(now, 18);
  const tomorrowMorning = at(addDays(now, 1), 11);
  const inTwoDays = at(addDays(now, 2), 11);
  const nextMonday = at(addDays(now, ((8 - now.getDay()) % 7) || 7), 11);
  return [
    { label: 'In 1 hour', date: inHour },
    ...(eveningToday.getTime() - now.getTime() > 90 * 60_000 ? [{ label: 'This evening', date: eveningToday }] : []),
    { label: 'Tomorrow morning', date: tomorrowMorning },
    { label: 'In 2 days', date: inTwoDays },
    { label: 'Next Monday', date: nextMonday },
  ];
}

/** Value for <input type="datetime-local"> in the user's local time. */
export function toLocalInput(d: Date | string): string {
  const x = typeof d === 'string' ? new Date(d) : d;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}T${p(x.getHours())}:${p(x.getMinutes())}`;
}

/** "Today 4:30 PM", "Tomorrow 11:00 AM", "Mon 12 Oct, 11:00 AM", with overdue flag. */
export function describeFollowup(iso: string | null): { text: string; tone: 'overdue' | 'today' | 'later' | 'none' } {
  if (!iso) return { text: 'No follow-up', tone: 'none' };
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  const startToday = at(now, 0);
  const startTomorrow = addDays(startToday, 1);
  if (d < now) {
    const sameDay = d >= startToday;
    return { text: sameDay ? `Today ${time} (late)` : `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${time} (late)`, tone: 'overdue' };
  }
  if (d < startTomorrow) return { text: `Today ${time}`, tone: 'today' };
  if (d < addDays(startTomorrow, 1)) return { text: `Tomorrow ${time}`, tone: 'later' };
  return { text: `${d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}, ${time}`, tone: 'later' };
}

export const telLink = (phone: string) => `tel:${phone}`;
export const whatsappLink = (phone: string, text = '') => `https://wa.me/${phone.replace(/\D/g, '')}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
